import { Space } from 'antd'
import { observer } from 'mobx-react-lite'

import { FormField } from '@/Core/UI/Form'
import { formatMoneyWithCurrency } from '@/Ship/Helpers/formatNumber'

import type { ReactNode } from 'react'

/** Те, що поле бере з ядрового `formContext`. */
export interface CostCurrencyFormContextLike {
    isEditable?: boolean
    values?: Record<string, unknown>
    getError?: (path: string) => unknown
}

export interface CostCurrencyFieldProps {
    formContext: CostCurrencyFormContextLike
    /** Поле суми (`approximateCostCargo`) */
    amount: string
    /** Поле валюти (`approximateCostCargoCurrency`) — select `Setting.ListCurrency` */
    currency: string
    /** Підпис: один на пару — сума без валюти не має сенсу */
    label: string
    isRequired?: boolean
}

/** Валюта без значення в перегляді — EUR, як у легасі (`SPEC-008` §4.8). */
const DEFAULT_CURRENCY = 'EUR'

/** Ширина селекта валюти: код і стрілка, решта рядка — сумі. */
const CURRENCY_WIDTH = 150

const codeOf = (value: unknown): string => {
    if (value !== null && typeof value === 'object') {
        const code = (value as { code?: unknown }).code

        if (typeof code === 'string' && code !== '') return code
    }

    return DEFAULT_CURRENCY
}

const amountOf = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value)

    return null
}

const errorText = (value: unknown): string | null => (typeof value === 'string' && value !== '' ? value : null)

/**
 * Поле суми й валюти без власного підпису й помилки — їх малює
 * `CostCurrencyField` один раз на пару.
 */
const Bare = observer(({ children }: { children?: ReactNode }) => <>{children}</>)

/**
 * «Вартість + валюта» (`SPEC-008` §4.8): сума й валюта в одному рядку, одна
 * помилка під обома.
 *
 * Легасі тримав пару саме так — сума й вузький селект валюти поруч, і людина
 * читала їх як одне значення. Розкладені окремими полями сітки, вони
 * опинялися б у різних стовпцях, а помилка «оберіть валюту» стояла б під
 * сумою сусіднього рядка.
 *
 * Перегляд — «1 234,50 EUR» (`formatMoneyWithCurrency`); валюти немає — EUR.
 *
 * ⚠️ Поле — окремий observer: воно читає значення, які редагують. Шаблон
 * картки цього робити не може (ядровий `FormField` перемонтовує форму на
 * кожне перемальовування шаблону, `SPEC-002` §4.2).
 *
 * Малюється всередині `<Form formContext>` картки — так само, як `FormField`.
 */
export const CostCurrencyField = observer(
    ({ formContext, amount, currency, label, isRequired = false }: CostCurrencyFieldProps) => {
        const values = formContext.values ?? {}

        if (formContext.isEditable !== true) {
            const sum = amountOf(values[amount])

            return (
                <div className="form-field-view">
                    <div className="field-view-label">{label}</div>
                    <div className={`field-view-value${sum === null ? ' is-empty' : ''}`}>
                        {sum === null ? '—' : formatMoneyWithCurrency(sum, codeOf(values[currency]))}
                    </div>
                </div>
            )
        }

        const error = errorText(formContext.getError?.(amount)) ?? errorText(formContext.getError?.(currency))

        return (
            <div className={`form-field form-field-default${error ? ' is-error' : ''}`}>
                <label className="field-label">
                    {isRequired ? <span className="field-required">*</span> : null}
                    <h2 className="field-label-title">{label}</h2>
                </label>
                <Space.Compact style={{ width: '100%' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <FormField name={amount} template={Bare} />
                    </div>
                    <div style={{ width: CURRENCY_WIDTH, flexShrink: 0 }}>
                        <FormField name={currency} template={Bare} />
                    </div>
                </Space.Compact>
                {error ? <small className="field-error">{error}</small> : null}
            </div>
        )
    }
)
